type IdLike = { _id?: unknown; id?: unknown; sub?: unknown; name?: unknown; label?: unknown };

const isObjectLike = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const toString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return undefined;
};

const toId = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (!isObjectLike(value)) return undefined;
  const candidate = (value as IdLike)._id ?? (value as IdLike).id ?? (value as IdLike).sub;
  if (typeof candidate === 'string') return candidate;
  if (typeof candidate === 'number') return String(candidate);
  return undefined;
};

const toName = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (!isObjectLike(value)) return undefined;
  const candidate = (value as IdLike).name ?? (value as IdLike).label;
  return typeof candidate === 'string' ? candidate : undefined;
};

const normalizeArrayValue = (value: unknown, mode: 'id' | 'name'): string | undefined => {
  if (typeof value === 'string') return value;
  const list = Array.isArray(value) ? value : [value];
  const normalized = list
    .map((item) => (mode === 'id' ? toId(item) : toName(item) ?? toString(item)))
    .filter((item): item is string => typeof item === 'string');
  if (normalized.length === 0) return undefined;
  return JSON.stringify(normalized);
};

const ALLOWED_KEYS = new Set([
  'startDate',
  'endDate',
  'campaigns',
  'units',
  'team members',
  'teammembers',
  'status',
  'type',
  'department',
  'outlet',
  'year',
]);

const ID_ARRAY_KEYS = new Set(['campaigns', 'team members', 'teammembers', 'type', 'outlet']);
const NAME_ARRAY_KEYS = new Set(['units', 'status', 'department']);

const shouldSkipParam = (key: string, rawValue: unknown): boolean =>
  !ALLOWED_KEYS.has(key) || rawValue === undefined || rawValue === null;

const toDateParamValue = (rawValue: unknown): string | undefined => {
  if (rawValue instanceof Date) {
    return rawValue.toISOString();
  }
  return toString(rawValue);
};

const toNormalizedParamValue = (key: string, rawValue: unknown): string | undefined => {
  if (key === 'startDate' || key === 'endDate') {
    return toDateParamValue(rawValue);
  }
  if (key === 'year') {
    return toString(rawValue);
  }
  if (ID_ARRAY_KEYS.has(key)) {
    return normalizeArrayValue(rawValue, 'id');
  }
  if (NAME_ARRAY_KEYS.has(key)) {
    return normalizeArrayValue(rawValue, 'name');
  }
  return toString(rawValue);
};

export const normalizeAnalyticsChartQuery = (input: unknown): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URLSearchParams) return input.toString();
  if (!isObjectLike(input)) return '';

  const params = new URLSearchParams();

  for (const [key, rawValue] of Object.entries(input)) {
    if (shouldSkipParam(key, rawValue)) continue;
    const value = toNormalizedParamValue(key, rawValue);
    if (value !== undefined) params.set(key, value);
  }

  return params.toString();
};
